import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Save,
  Palette,
  Mail,
  Headphones,
  Sparkles,
  ExternalLink,
  RotateCcw,
  FolderOpen,
} from "lucide-react";
import type { Channel, Settings } from "@db/schema";
import {
  intakePaymentHoldPolicyValues,
  type IntakePaymentHoldPolicy,
} from "@shared/contracts/intake";
import logoDefault from "@/assets/logo-default.png";
import { FileSlackRulesCard } from "@/components/admin/file-slack-rules";
import React from "react";

export default function AdminSettings() {
  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get("tab") || "branding";

  const { data: settings, isLoading } = useQuery<Settings>({
    queryKey: ["/api/billing/settings"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <Tabs defaultValue={initialTab} className="space-y-6">
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="branding">Branding</TabsTrigger>
        <TabsTrigger value="billing">Billing</TabsTrigger>
        <TabsTrigger value="support">Support</TabsTrigger>
        <TabsTrigger value="intake">Intake</TabsTrigger>
        <TabsTrigger value="files">Files</TabsTrigger>
      </TabsList>

      <TabsContent value="general">
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
            <CardDescription>Core system configuration.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Default Currency</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {settings?.defaultCurrency ?? "GBP"}
                </p>
              </div>
              <div>
                <Label>Default Timezone</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {settings?.defaultTimezone ?? "Europe/London"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="branding">
        {settings && <BrandingTab settings={settings} />}
      </TabsContent>

      <TabsContent value="billing">
        {settings && <BillingTab settings={settings} />}
      </TabsContent>

      <TabsContent value="support">
        {settings && <SupportTab settings={settings} />}
      </TabsContent>

      <TabsContent value="intake">
        {settings && <IntakeTab settings={settings} />}
      </TabsContent>

      <TabsContent value="files">
        {settings && <FilesTab settings={settings} />}
      </TabsContent>
    </Tabs>
  );
}

// ─── Billing Tab ───────────────────────────────────────────────
function BillingTab({ settings }: { settings: Settings }) {
  const { toast } = useToast();

  const [emailProvider, setEmailProvider] = useState(
    settings.emailProvider ?? "smtp",
  );
  const [smtpHost, setSmtpHost] = useState(settings.smtpHost ?? "");
  const [smtpPort, setSmtpPort] = useState(settings.smtpPort ?? "587");
  const [smtpUsername, setSmtpUsername] = useState(settings.smtpUsername ?? "");
  const [smtpPassword, setSmtpPassword] = useState(settings.smtpPassword ?? "");
  const [smtpFromName, setSmtpFromName] = useState(settings.smtpFromName ?? "");
  const [smtpFromEmail, setSmtpFromEmail] = useState(
    settings.smtpFromEmail ?? "",
  );
  const [emailitApiKey, setEmailitApiKey] = useState(
    settings.emailitApiKey ?? "",
  );
  const [emailsEnabled, setEmailsEnabled] = useState(
    settings.emailsEnabled ?? true,
  );
  const [smallTaskFee, setSmallTaskFee] = useState(
    settings.smallTaskFee ?? "0",
  );
  const [mediumTaskFee, setMediumTaskFee] = useState(
    settings.mediumTaskFee ?? "0",
  );
  const [hourlyRate, setHourlyRate] = useState(settings.hourlyRate ?? "0");
  const [taxRate, setTaxRate] = useState(
    settings.defaultTaxRatePercent ?? "20",
  );
  const [schedulingLinkUrl, setSchedulingLinkUrl] = useState(
    settings.schedulingLinkUrl ?? "",
  );
  const [annualDays, setAnnualDays] = useState(
    (settings.annualReminderDays ?? []).join(", "),
  );
  const [monthlyDays, setMonthlyDays] = useState(
    (settings.monthlyReminderDays ?? []).join(", "),
  );

  const mutation = useMutation({
    mutationFn: async (data: Partial<Settings>) => {
      const res = await apiRequest("PATCH", "/api/billing/settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/settings"] });
      toast({
        title: "Billing settings saved",
        description: "Changes applied successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save billing settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      emailProvider,
      smtpHost: smtpHost || null,
      smtpPort: smtpPort || null,
      smtpUsername: smtpUsername || null,
      smtpPassword: smtpPassword || null,
      smtpFromName: smtpFromName || null,
      smtpFromEmail: smtpFromEmail || null,
      emailitApiKey: emailitApiKey || null,
      emailsEnabled,
      smallTaskFee,
      mediumTaskFee,
      hourlyRate,
      defaultTaxRatePercent: taxRate,
      schedulingLinkUrl: schedulingLinkUrl || null,
      annualReminderDays: annualDays
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean),
      monthlyReminderDays: monthlyDays
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean),
    } as Partial<Settings>);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <CardTitle>Email Delivery</CardTitle>
          </div>
          <CardDescription>
            Choose SMTP or EmailIt API for outbound email. The global email
            toggle applies to both providers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Emails</Label>
              <p className="text-xs text-muted-foreground">
                Toggle all outbound email
              </p>
            </div>
            <Switch
              checked={emailsEnabled}
              onCheckedChange={setEmailsEnabled}
            />
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Email Provider</Label>
              <Select value={emailProvider} onValueChange={setEmailProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="smtp">SMTP</SelectItem>
                  <SelectItem value="emailit_api">EmailIt API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {emailProvider === "emailit_api" && (
              <div className="space-y-2">
                <Label>EmailIt API Key</Label>
                <Input
                  type="password"
                  value={emailitApiKey}
                  onChange={(e) => setEmailitApiKey(e.target.value)}
                  placeholder="sk_live_..."
                />
              </div>
            )}
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>From Name</Label>
              <Input
                value={smtpFromName}
                onChange={(e) => setSmtpFromName(e.target.value)}
                placeholder="HeyHiHello Hosting"
              />
            </div>
            <div className="space-y-2">
              <Label>From Email</Label>
              <Input
                value={smtpFromEmail}
                onChange={(e) => setSmtpFromEmail(e.target.value)}
                placeholder="billing@example.com"
              />
            </div>
          </div>

          {emailProvider === "smtp" && (
            <>
              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>SMTP Host</Label>
                  <Input
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port</Label>
                  <Input
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    placeholder="587"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Username</Label>
                  <Input
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Password</Label>
                  <Input
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Billing Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Default Tax Rate (%)</Label>
              <Input
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="20"
              />
            </div>
            <div className="space-y-2">
              <Label>Scheduling Link URL</Label>
              <Input
                value={schedulingLinkUrl}
                onChange={(e) => setSchedulingLinkUrl(e.target.value)}
                placeholder="https://calendly.com/..."
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Annual Reminder Days (comma-separated)</Label>
              <Input
                value={annualDays}
                onChange={(e) => setAnnualDays(e.target.value)}
                placeholder="45, 30, 15, 7, 3"
              />
            </div>
            <div className="space-y-2">
              <Label>Monthly Reminder Days (comma-separated)</Label>
              <Input
                value={monthlyDays}
                onChange={(e) => setMonthlyDays(e.target.value)}
                placeholder="15, 7, 3, 1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Work Request Fees</CardTitle>
          <CardDescription>
            Set default pricing for work requests and tasks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Small Task Fee ({settings.defaultCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                value={smallTaskFee}
                onChange={(e) => setSmallTaskFee(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Medium Task Fee ({settings.defaultCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                value={mediumTaskFee}
                onChange={(e) => setMediumTaskFee(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hourly Rate ({settings.defaultCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {mutation.isPending ? "Saving..." : "Save Billing Settings"}
        </Button>
      </div>
    </div>
  );
}

// ─── Support Tab ───────────────────────────────────────────────
function SupportTab({ settings }: { settings: Settings }) {
  const { toast } = useToast();
  const { data: channels } = useQuery<Channel[]>({
    queryKey: ["/api/support/channels"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const [pollingFrequency, setPollingFrequency] = useState(
    String(settings.pollingFrequencyMinutes ?? 10),
  );
  const [nylasWebhookBaseUrl, setNylasWebhookBaseUrl] = useState(
    settings.nylasWebhookBaseUrl ?? "",
  );
  const [defaultIntakeChannelId, setDefaultIntakeChannelId] = useState(
    settings.defaultIntakeChannelId ?? "none",
  );

  const mutation = useMutation({
    mutationFn: async (data: Partial<Settings>) => {
      const res = await apiRequest("PATCH", "/api/billing/settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/settings"] });
      toast({
        title: "Support settings saved",
        description: "Changes applied successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save support settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      pollingFrequencyMinutes: parseInt(pollingFrequency) || 10,
      nylasWebhookBaseUrl: nylasWebhookBaseUrl || null,
      defaultIntakeChannelId:
        defaultIntakeChannelId === "none" ? null : defaultIntakeChannelId,
    } as Partial<Settings>);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Headphones className="h-5 w-5 text-primary" />
            <CardTitle>Support Settings</CardTitle>
          </div>
          <CardDescription>
            Configure email polling and webhook settings for the support module.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Polling Frequency (minutes)</Label>
              <Input
                type="number"
                value={pollingFrequency}
                onChange={(e) => setPollingFrequency(e.target.value)}
                placeholder="10"
                min={1}
              />
              <p className="text-xs text-muted-foreground">
                How often to check connected mailboxes for new messages.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Nylas Webhook Base URL</Label>
              <Input
                value={nylasWebhookBaseUrl}
                onChange={(e) => setNylasWebhookBaseUrl(e.target.value)}
                placeholder="https://your-domain.com"
              />
              <p className="text-xs text-muted-foreground">
                Base URL for Nylas webhooks (used when connecting mailboxes).
              </p>
            </div>
            <div className="space-y-2">
              <Label>Default Intake Channel</Label>
              <Select
                value={defaultIntakeChannelId}
                onValueChange={setDefaultIntakeChannelId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No default channel</SelectItem>
                  {channels?.map((channel) => (
                    <SelectItem key={channel.id} value={channel.id}>
                      {channel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Work-request-created tickets use this channel and require an active mailbox.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SLA Configuration</CardTitle>
          <CardDescription>
            SLA policies and priority mappings are managed per-channel.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <a href="/admin/channels">
              Manage Channels & SLAs{" "}
              <ExternalLink className="ml-2 h-3 w-3" />
            </a>
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {mutation.isPending ? "Saving..." : "Save Support Settings"}
        </Button>
      </div>
    </div>
  );
}

// ─── Intake Tab ────────────────────────────────────────────────

const DEFAULT_SYSTEM_PROMPT = `You are a senior UX/dev/creative triage lead at HeyHiHello.
Classify the request, extract key requirements, list assumptions, call out missing info, and estimate effort in hours for ONE discrete task.

Output a single JSON object with these fields:
- category: the request category
- scope_summary: array of 2-4 bullet points describing the work
- assumptions: array of assumptions made
- missing_info: array of information still needed
- estimate_hours_number: numeric hours estimate
- estimate_bucket: "small" (≤0.5h) | "medium" (>0.5h and ≤1h) | "large" (>1h)
- recommended_next_steps: string describing next actions
- confidence_score: number 0.0-1.0 where 1.0 means highly confident in estimate, 0.0 means very uncertain
- help_docs: array of {title, url, relevance} for any relevant self-service documentation (use from Knowledgebase Context if provided)
- knowledgebase_match: {matched: boolean, entry_id?: string, adjusted_estimate_minutes?: number, self_service_possible?: boolean}
- covered_by_service: boolean – true if the work falls under an existing retainer, managed hosting, or maintenance plan (see Client Services Context if provided)
- coverage_reason: string – if covered_by_service is true, explain which service covers this work (e.g. "Covered by Development Retainer")

When Knowledgebase Context is provided:
1. Check if any entries closely match the client's issue
2. If matched, use the entry's estimated_minutes for a more accurate estimate
3. Include any help_doc_url from matched entries in the help_docs array
4. Set self_service_possible to true if the help doc could resolve the issue without HeyHiHello intervention

When Client Services Context is provided:
1. Review the client's active services for retainers, maintenance plans, managed hosting, or dev support agreements
2. If the requested work type falls under one of these services, set covered_by_service to true
3. Services containing keywords like "retainer", "maintenance", "managed", "support", "care plan" typically cover routine work requests
4. Creative/design requests are only covered if a specific design retainer or creative retainer exists
5. New projects or large builds (>5h) are generally NOT covered by standard retainers

NEVER commit to cost. Always mark scope as "provisional – human review required".
Be concise but thorough. Focus on actionable insights.`;

function IntakeTab({ settings }: { settings: Settings }) {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState(
    (settings as any).aiSystemPrompt ?? "",
  );
  const [paymentHoldPolicy, setPaymentHoldPolicy] =
    useState<IntakePaymentHoldPolicy>(
      settings.intakePaymentHoldPolicy ?? "required",
    );
  const [hasChanges, setHasChanges] = useState(false);

  const mutation = useMutation({
    mutationFn: async (data: Partial<Settings>) => {
      const res = await apiRequest("PATCH", "/api/billing/settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/intake/pricing"] });
      setHasChanges(false);
      toast({ title: "Intake settings saved" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to save",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      aiSystemPrompt: prompt || null,
      intakePaymentHoldPolicy: paymentHoldPolicy,
    } as any);
  };

  const handleReset = () => {
    setPrompt("");
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Payment Hold Behaviour</CardTitle>
          <CardDescription>
            Control whether the request form should offer or require a card
            authorisation hold before submission for billable requests.
            Covered services will always skip payment hold.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Card Hold Policy</Label>
            <Select
              value={paymentHoldPolicy}
              onValueChange={(value: IntakePaymentHoldPolicy) => {
                setPaymentHoldPolicy(value);
                setHasChanges(true);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select hold policy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disabled">
                  No card hold on request submission
                </SelectItem>
                <SelectItem value="optional">
                  Optional card hold before submission
                </SelectItem>
                <SelectItem value="optional_if_urgent">
                  Optional, but required for urgent requests
                </SelectItem>
                <SelectItem value="required">
                  Always require a card hold
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            {paymentHoldPolicy === "disabled" && (
              <p>
                Clients can submit billable service requests without seeing or
                completing a card hold.
              </p>
            )}
            {paymentHoldPolicy === "optional" && (
              <p>
                Clients can submit without a card hold, but may choose to add
                one before sending the request.
              </p>
            )}
            {paymentHoldPolicy === "optional_if_urgent" && (
              <p>
                Clients can submit without a card hold unless they mark the
                request as urgent, in which case a hold is required.
              </p>
            )}
            {paymentHoldPolicy === "required" && (
              <p>
                Billable small and medium requests require a card hold before
                the request can be submitted.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>AI Intake Settings</CardTitle>
          </div>
          <CardDescription>
            Configure the system prompt used by the AI scoping assistant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-muted-foreground">Model</Label>
              <p className="text-sm mt-1">gpt-4.1-mini</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Temperature</Label>
              <p className="text-sm mt-1">0.2 (low — deterministic)</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            To change the OpenAI model or API key, update the{" "}
            <code className="text-xs">OPENAI_API_KEY</code> environment variable
            in your deployment settings.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>System Prompt</CardTitle>
          <CardDescription>
            This prompt tells the AI how to analyze and scope incoming requests.
            Leave blank to use the built-in default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              setHasChanges(true);
            }}
            placeholder={DEFAULT_SYSTEM_PROMPT}
            className="min-h-[300px] font-mono text-xs leading-relaxed"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={!prompt}
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Reset to Default
              </Button>
              <span className="text-xs text-muted-foreground">
                {prompt
                  ? "Using custom prompt"
                  : "Using built-in default"}
              </span>
            </div>
            <Button
              onClick={handleSave}
              disabled={mutation.isPending || !hasChanges}
              size="sm"
            >
              {mutation.isPending ? "Saving…" : "Save Intake Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Knowledgebase</CardTitle>
          <CardDescription>
            Manage knowledgebase entries used for AI scoping context and
            self-service help docs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <a href="/admin/knowledgebase">
              Manage Knowledgebase{" "}
              <ExternalLink className="ml-2 h-3 w-3" />
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Branding Tab ──────────────────────────────────────────────
function BrandingTab({ settings }: { settings: Settings }) {
  const { toast } = useToast();

  const [brandName, setBrandName] = useState(
    settings.brandName ?? "HeyHiHello",
  );
  const [logoUrl, setLogoUrl] = useState(settings.logoUrl ?? "");
  const [primaryColor, setPrimaryColor] = useState(
    settings.primaryColorHsl ?? "348.37 100% 55.49%",
  );
  const [primaryFgColor, setPrimaryFgColor] = useState(
    settings.primaryFgColorHsl ?? "0 0% 100%",
  );
  const [accentColor, setAccentColor] = useState(
    settings.accentColorHsl ?? "211.5789 51.3514% 92.7451%",
  );
  const [accentFgColor, setAccentFgColor] = useState(
    settings.accentFgColorHsl ?? "348.37 100% 55.49%",
  );
  const [borderRadius, setBorderRadius] = useState(
    parseFloat(settings.borderRadiusRem ?? "1.3"),
  );
  const [headingFont, setHeadingFont] = useState(
    settings.headingFont ?? "DM Sans",
  );
  const [faviconUrl, setFaviconUrl] = useState(settings.faviconUrl ?? "");

  const mutation = useMutation({
    mutationFn: async (data: Partial<Settings>) => {
      const res = await apiRequest("PATCH", "/api/billing/settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/settings"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/billing/settings/branding"],
      });
      toast({
        title: "Branding saved",
        description: "Changes applied successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save branding settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      brandName,
      logoUrl: logoUrl || null,
      primaryColorHsl: primaryColor,
      primaryFgColorHsl: primaryFgColor,
      accentColorHsl: accentColor,
      accentFgColorHsl: accentFgColor,
      borderRadiusRem: borderRadius.toString(),
      headingFont,
      faviconUrl: faviconUrl || null,
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            <CardTitle>Branding</CardTitle>
          </div>
          <CardDescription>
            Customise your logo, colours, and typography. Changes apply across
            all pages for all users.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="brandName">Brand Name</Label>
            <Input
              id="brandName"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="HeyHiHello"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="logoUrl">Logo URL</Label>
            <Input
              id="logoUrl"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
            />
            <div className="mt-2 flex items-center gap-4">
              <p className="text-xs text-muted-foreground">Preview:</p>
              <img
                src={logoUrl || logoDefault}
                alt="Logo preview"
                className="h-10 object-contain bg-card border rounded-md px-3 py-1"
              />
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Colour (HSL)</Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-8 w-8 rounded-md border shrink-0"
                  style={{ backgroundColor: `hsl(${primaryColor})` }}
                />
                <Input
                  id="primaryColor"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="348.37 100% 55.49%"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="primaryFgColor">Primary Foreground (HSL)</Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-8 w-8 rounded-md border shrink-0"
                  style={{ backgroundColor: `hsl(${primaryFgColor})` }}
                />
                <Input
                  id="primaryFgColor"
                  value={primaryFgColor}
                  onChange={(e) => setPrimaryFgColor(e.target.value)}
                  placeholder="0 0% 100%"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accentColor">Accent Colour (HSL)</Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-8 w-8 rounded-md border shrink-0"
                  style={{ backgroundColor: `hsl(${accentColor})` }}
                />
                <Input
                  id="accentColor"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="211.5789 51.3514% 92.7451%"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accentFgColor">Accent Foreground (HSL)</Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-8 w-8 rounded-md border shrink-0"
                  style={{ backgroundColor: `hsl(${accentFgColor})` }}
                />
                <Input
                  id="accentFgColor"
                  value={accentFgColor}
                  onChange={(e) => setAccentFgColor(e.target.value)}
                  placeholder="348.37 100% 55.49%"
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Border Radius</Label>
              <span className="text-sm text-muted-foreground">
                {borderRadius.toFixed(2)}rem
              </span>
            </div>
            <Slider
              value={[borderRadius]}
              onValueChange={([v]: number[]) => setBorderRadius(v)}
              min={0.25}
              max={2}
              step={0.05}
            />
            <div className="flex items-center gap-4 mt-2">
              <p className="text-xs text-muted-foreground">Preview:</p>
              <div
                className="h-10 w-24 bg-primary"
                style={{ borderRadius: `${borderRadius}rem` }}
              />
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Heading Font</Label>
              <Select value={headingFont} onValueChange={setHeadingFont}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DM Sans">DM Sans</SelectItem>
                  <SelectItem value="Inter">Inter</SelectItem>
                  <SelectItem value="system-ui">System Default</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="faviconUrl">Favicon URL</Label>
              <Input
                id="faviconUrl"
                value={faviconUrl}
                onChange={(e) => setFaviconUrl(e.target.value)}
                placeholder="https://example.com/favicon.ico"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Live Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="rounded-lg border p-6 space-y-4"
            style={
              {
                "--preview-primary": `hsl(${primaryColor})`,
                "--preview-fg": `hsl(${primaryFgColor})`,
                "--preview-radius": `${borderRadius}rem`,
              } as React.CSSProperties
            }
          >
            <div className="flex items-center gap-3">
              <img
                src={logoUrl || logoDefault}
                alt="Preview logo"
                className="h-8 object-contain"
              />
              <span
                className="text-lg font-bold"
                style={{ fontFamily: `"${headingFont}", sans-serif` }}
              >
                {brandName}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                className="px-4 py-2 text-sm font-medium"
                style={{
                  backgroundColor: `hsl(${primaryColor})`,
                  color: `hsl(${primaryFgColor})`,
                  borderRadius: `${borderRadius}rem`,
                }}
              >
                Primary Button
              </button>
              <button
                className="px-4 py-2 text-sm font-medium border"
                style={{
                  borderColor: `hsl(${primaryColor})`,
                  color: `hsl(${primaryColor})`,
                  borderRadius: `${borderRadius}rem`,
                }}
              >
                Outline Button
              </button>
            </div>
            <p
              className="text-sm"
              style={{ fontFamily: `"${headingFont}", sans-serif` }}
            >
              This is how headings will look with {headingFont}.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {mutation.isPending ? "Saving..." : "Save Branding"}
        </Button>
      </div>
    </div>
  );
}

function parseNotifyEmails(raw: string): string[] {
  const emails = raw
    .split(/[,;\n]+/)
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(emails)];
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

// ─── Files Tab ─────────────────────────────────────────────────
function FilesTab({ settings }: { settings: Settings }) {
  const { toast } = useToast();
  const [slackEnabled, setSlackEnabled] = useState(
    settings.filesSlackAlertsEnabled ?? true,
  );
  const [notifyEmailsRaw, setNotifyEmailsRaw] = useState(
    (settings.filesNotifyEmails ?? []).join("\n"),
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const emails = parseNotifyEmails(notifyEmailsRaw);
      const invalid = emails.filter((email) => !isValidEmail(email));
      if (invalid.length) {
        throw new Error(`Invalid email(s): ${invalid.join(", ")}`);
      }
      const res = await apiRequest("PATCH", "/api/billing/settings", {
        filesSlackAlertsEnabled: slackEnabled,
        filesNotifyEmails: emails,
      });
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["/api/billing/settings"],
      });
      toast({ title: "File notification settings saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save file settings",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            File upload notifications
          </CardTitle>
          <CardDescription>
            One notification per finished upload batch, not per file. With no
            routing rule, the summary goes to the Slack incoming webhook.
            Rules below can send it to channels the bot can post in, or mute
            it. The Files switch above turns every file Slack alert off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-1 pr-4">
              <Label htmlFor="files-slack-toggle">Slack notifications</Label>
              <p className="text-sm text-muted-foreground">
                Master switch for file upload Slack alerts. Off sends nothing,
                including routed channels. On uses a matching rule, or the
                incoming webhook when nothing matches.
              </p>
            </div>
            <Switch
              id="files-slack-toggle"
              checked={slackEnabled}
              onCheckedChange={setSlackEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="files-notify-emails">Notification emails</Label>
            <Textarea
              id="files-notify-emails"
              value={notifyEmailsRaw}
              onChange={(event) => setNotifyEmailsRaw(event.target.value)}
              placeholder={"ops@example.com\nteam@example.com"}
              rows={5}
            />
            <p className="text-xs text-muted-foreground">
              One address per line (commas also fine). Leave empty to disable
              email alerts. Uses your Billing email sender settings. Email
              alerts stay global and are not routed per channel.
            </p>
          </div>
          <FileSlackRulesCard />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {mutation.isPending ? "Saving..." : "Save file settings"}
        </Button>
      </div>
    </div>
  );
}
